// Copyright (c) 2025 Goffy. All Rights Reserved. This file belongs to Goffy.

import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Platform, Image, useWindowDimensions, type ImageSourcePropType } from 'react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useRouter, useRootNavigationState, useLocalSearchParams } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import GameWeightControl, { WEIGHT_STEPS } from '@/components/GameWeightControl';
import MeritDistributionBar from '@/features/tour-edit/components/shared/MeritDistributionBar';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { t } from '@/i18n';
import { getSharedBffWebSocket, PlayersErrorEvent, PlayersSnapshotEvent, TourUpdateEvent, TourCreatedEvent } from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildMediaUrl } from '@/services/mediaUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import { setCurrentTourId } from '@/services/currentTour';
import { setCurrentHole, setCurrentRoundId } from '@/services/currentRound';
import { useUser } from '@/app/user-context';
import { UserBadge } from '@/components/UserBadge';
import { useRoundState } from '@/stores/roundState';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { groupItems } from '@/features/game-definitions/groups';
import { formatMeritLadder, getMeritLadder, useGameScoringRules } from '@/features/game-definitions/merits';
import { ensureTournamentGamesSelected, toggleSelectableGame } from '@/services/tournamentGameSelection';
import { buildRoundRouteHref, resolvePostGameSelectionRoute } from '@/services/roundNavigation';
import appColors from '@/theme/colors';

type Player = {
  id: string;
  name: string;
  createdAt: string;
};

type PlayersResponse = {
  players?: Player[];
  tourId?: string;
  Players?: Player[];
  TourId?: string;
};

type RoundResponse = {
  courseName?: string;
  CourseName?: string;
  tourId?: string;
  TourId?: string;
  status?: string;
  Status?: string;
  gameDefinitionIds?: string[];
  GameDefinitionIds?: string[];
  gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
  GameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
};

type TourDetailsResponse = {
  tourName?: string;
  TourName?: string;
  name?: string;
  Name?: string;
  gameDefinitionIds?: string[];
  GameDefinitionIds?: string[];
};

type GameDefinition = {
  id: string;
  name: string;
  defaultWeightPercent?: number;
  info?: string;
  description?: string;
  competitionType?: string;
  status?: string;
  image?: string;
  video?: string;
  videoWebWidth?: number;
  videoWebHeight?: number;
  videoMobileWidth?: number;
  videoMobileHeight?: number;
};

type GameRecommendation = {
  gameDefinitionId?: string;
  GameDefinitionId?: string;
  priority?: number;
  Priority?: number;
};

const PHONE_GAME_IMAGE_BREAKPOINT = 640;
const WEB_GAME_IMAGE_BREAKPOINT = 1100;
const WEB_FOUR_COLUMN_BREAKPOINT = 1200;
const MEDIA_PAGE_PATH = '/round/[roundId]/select-games';
const DEFAULT_GAME_WEIGHT_PERCENT = 100;
const GAME_IMAGE_MAP: Record<string, ImageSourcePropType> = {
  'best-ball': getAssetImageSource('images/games/best-ball-select.png', 'game-select', MEDIA_PAGE_PATH),
  '3-putts': getAssetImageSource('images/games/3-putts-select.png', 'game-select', MEDIA_PAGE_PATH),
  bunker: getAssetImageSource('images/games/bunker-select.png', 'game-select', MEDIA_PAGE_PATH),
  'high-low': getAssetImageSource('images/games/high-low-select.png', 'game-select', MEDIA_PAGE_PATH),
  mystery: getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH),
  penalty: getAssetImageSource('images/games/penalty-select.png', 'game-select', MEDIA_PAGE_PATH),
  skins: getAssetImageSource('images/games/skins-select.png', 'game-select', MEDIA_PAGE_PATH),
  stableford: getAssetImageSource('images/games/stableford-select.png', 'game-select', MEDIA_PAGE_PATH),
  'stroke-play': getAssetImageSource('images/games/stableford-select.png', 'game-select', MEDIA_PAGE_PATH),
  water: getAssetImageSource('images/games/water-select.png', 'game-select', MEDIA_PAGE_PATH),
};

export default function SelectGamesScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { width: viewportWidth } = useWindowDimensions();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { user } = useUser();
  const { debug: debugParam, roundId: roundIdParam, source: sourceParam, tourId: tourIdParam } = useLocalSearchParams<{
    debug?: string;
    roundId?: string;
    source?: string;
    tourId?: string;
  }>();
  const storedRoundId = useRoundState((state) => state.roundId);
  const [tourId, setTourId] = useState<string | null>(null);
  const [tourName, setTourName] = useState<string | null>(null);
  const [courseName, setCourseName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameDefs, setGameDefs] = useState<GameDefinition[]>([]);
  const [gamesError, setGamesError] = useState<string | null>(null);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  const [weightsByGameId, setWeightsByGameId] = useState<Record<string, number>>({});
  const [tournamentGameIds, setTournamentGameIds] = useState<string[]>([]);
  const [recommendedPriorityByGameId, setRecommendedPriorityByGameId] = useState<Record<string, number>>({});
  const [players, setPlayers] = useState<Player[]>([]);
  const [roundPlayerCount, setRoundPlayerCount] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);
  const [statusNote, setStatusNote] = useState<string>('Laddar spel och spelare...');
  const [roundStatus, setRoundStatus] = useState<string | null>(null);
  const gamesLoadedRef = useRef(false);
  const prefilledGamesRef = useRef(false);
  const prefilledWeightsRef = useRef(false);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const sourceFromParams = useMemo(() => {
    const raw = Array.isArray(sourceParam) ? sourceParam[0] : sourceParam;
    return (raw ?? '').toLowerCase();
  }, [sourceParam]);
  const tourIdFromParams = useMemo(() => {
    const raw = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
    return raw ?? null;
  }, [tourIdParam]);
  const openedFromRoundStart = sourceFromParams === 'roundstart';
  const openedFromHole = sourceFromParams === 'hole';
  const roundId = roundIdFromParams ?? storedRoundId;
  const pendingRoundRequestIdRef = useRef<string | null>(null);
  const roundAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gameImageSize = useMemo(() => {
    if (viewportWidth > 0 && viewportWidth <= PHONE_GAME_IMAGE_BREAKPOINT) {
      return { width: 96, height: 96 };
    }
    if (Platform.OS === 'web' && viewportWidth >= WEB_GAME_IMAGE_BREAKPOINT) {
      return { width: 168, height: 168 };
    }
    return { width: 132, height: 132 };
  }, [viewportWidth]);
  const gameImageStyle = useMemo(
    () => ({ width: gameImageSize.width, height: gameImageSize.height, alignSelf: 'center' as const }),
    [gameImageSize]
  );
  const gameTileStyle = useMemo(
    () => ({ width: Platform.OS === 'web' && viewportWidth >= WEB_FOUR_COLUMN_BREAKPOINT ? '23.5%' : '48%' }),
    [viewportWidth]
  );
  const roundIdIsValid = useMemo(() => !!roundId && isGuid(roundId), [roundId]);
  const isRoundAlreadyActive = useMemo(() => {
    const normalized = (roundStatus ?? '').toLowerCase();
    return normalized === 'active' || normalized === 'playing';
  }, [roundStatus]);
  const tournamentGameIdSet = useMemo(() => new Set(tournamentGameIds), [tournamentGameIds]);
  const chooseGameImage = useMemo(() => getAssetImageSource('images/select-games.png', 'hero', MEDIA_PAGE_PATH), []);
  const sortedGameDefs = useMemo(() => {
    const byName = (game: GameDefinition) => getGameDisplayName(game, t('selectGames.gameNameFallback')).toLowerCase();
    return [...gameDefs].sort((a, b) => {
      const aRecommended = Number.isFinite(recommendedPriorityByGameId[a.id]);
      const bRecommended = Number.isFinite(recommendedPriorityByGameId[b.id]);
      if (aRecommended !== bRecommended) {
        return aRecommended ? -1 : 1;
      }
      if (aRecommended && bRecommended) {
        const priorityDiff = (recommendedPriorityByGameId[a.id] ?? Number.MAX_SAFE_INTEGER)
          - (recommendedPriorityByGameId[b.id] ?? Number.MAX_SAFE_INTEGER);
        if (priorityDiff !== 0) {
          return priorityDiff;
        }
      }
      return byName(a).localeCompare(byName(b));
    });
  }, [gameDefs, recommendedPriorityByGameId]);
  // Bucket the (already sorted) games into their visual groups; per-group order is preserved, so
  // the recommended-first / alphabetical sort still applies within each group.
  const groupedGameDefs = useMemo(
    () => groupItems(sortedGameDefs, (game) => game.id, (game) => game.name),
    [sortedGameDefs]
  );
  const effectivePlayerCount = useMemo(() => {
    if (typeof roundPlayerCount === 'number' && roundPlayerCount > 0) {
      return roundPlayerCount;
    }
    return players.length;
  }, [players.length, roundPlayerCount]);
  const recommendationPlayerCount = useMemo(() => {
    if (effectivePlayerCount >= 6) return 6;
    if (effectivePlayerCount === 5) return 4;
    return effectivePlayerCount;
  }, [effectivePlayerCount]);
  const recommendationPlayerCountLabel = useMemo(() => {
    if (effectivePlayerCount >= 6) return '6+';
    return `${recommendationPlayerCount}`;
  }, [effectivePlayerCount, recommendationPlayerCount]);
  const getRecommendationRank = (gameId: string) => {
    const priority = recommendedPriorityByGameId[gameId];
    if (!Number.isFinite(priority)) return null;
    return Math.max(1, Math.trunc(priority) + 1);
  };

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId);
    }
  }, [tourId]);

  useEffect(() => {
    if (tourIdFromParams) {
      setTourId((prev) => prev ?? tourIdFromParams);
    }
  }, [tourIdFromParams]);

  useEffect(() => {
    if (roundId) {
      setCurrentRoundId(roundId);
    }
  }, [roundId]);

  useEffect(() => {
    if (roundIdFromParams && roundIdFromParams !== storedRoundId) {
      setCurrentRoundId(roundIdFromParams);
    }
  }, [roundIdFromParams, storedRoundId]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'select-games',
      }),
    [apiBaseUrl, user?.token]
  );

  const closeInfo = () => {
    setInfoOpen(false);
    setInfoGame(null);
  };

  useEffect(() => {
    if (!isNavigationReady || !user?.token) {
      setLoading(false);
      return;
    }
    const fetchSnapshot = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error(`${t('players.loadError')} (${response.status})`);
        }
        const data = (await response.json()) as PlayersResponse;
        const incomingPlayers = data.players ?? data.Players ?? [];
        setPlayers(incomingPlayers);
        const incomingTourId = data.tourId ?? data.TourId;
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
        setError(null);
        setStatusNote('');
      } catch {
        setError(t('players.loadError'));
        setStatusNote('Kunde inte hämta spelare.');
      } finally {
        setLoading(false);
      }
    };

    const fetchGames = async () => {
      if (gamesLoadedRef.current && !gamesError) {
        return;
      }
      if (!user?.token) {
        return;
      }
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          throw new Error();
        }
        const data = (await response.json()) as GameDefinition[];
        const normalized = data.map((g) => ({
          ...g,
          defaultWeightPercent:
            g.defaultWeightPercent ?? (g as { DefaultWeightPercent?: number }).DefaultWeightPercent ?? undefined,
          competitionType: g.competitionType ?? (g as { CompetitionType?: string }).CompetitionType ?? undefined,
          status: g.status ?? (g as { Status?: string }).Status ?? undefined,
          image: g.image ?? (g as { Image?: string }).Image ?? undefined,
          video: g.video ?? (g as { Video?: string }).Video ?? undefined,
          videoWebWidth: g.videoWebWidth ?? (g as { VideoWebWidth?: number }).VideoWebWidth ?? undefined,
          videoWebHeight: g.videoWebHeight ?? (g as { VideoWebHeight?: number }).VideoWebHeight ?? undefined,
          videoMobileWidth: g.videoMobileWidth ?? (g as { VideoMobileWidth?: number }).VideoMobileWidth ?? undefined,
          videoMobileHeight: g.videoMobileHeight ?? (g as { VideoMobileHeight?: number }).VideoMobileHeight ?? undefined,
        }));
        const enabledGames = normalized.filter((g) => !g.status || (g.status ?? '').toLowerCase() === 'enabled');
        const resolvedGames = enabledGames.length > 0 ? enabledGames : normalized;
        setGameDefs(resolvedGames);
        setGamesError(null);
        gamesLoadedRef.current = true;
        setStatusNote((prev) => (prev.includes('Kunde inte') ? `Hittade ${resolvedGames.length} spel.` : prev));
      } catch {
        setGamesError(t('selectGames.gameLoadError'));
        setStatusNote('Kunde inte hämta speltyper.');
        gamesLoadedRef.current = false;
      }
    };

    fetchSnapshot();
    fetchGames();

    const clearRoundAckTimeout = () => {
      if (roundAckTimeoutRef.current) {
        clearTimeout(roundAckTimeoutRef.current);
        roundAckTimeoutRef.current = null;
      }
    };

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const unsubscribeMessages = socket.onMessage((message) => {
      const type = (message as PlayersSnapshotEvent).type ?? (message as PlayersErrorEvent).type ?? (message as TourUpdateEvent).type;
      if (type === 'players_snapshot') {
        const payload = message as PlayersSnapshotEvent;
        const incomingTourId = payload.tourId ?? payload.TourId;
        setPlayers(payload.players ?? payload.Players ?? []);
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
        setError(null);
        setStatusNote(`Websocket: ${payload.players?.length ?? payload.Players?.length ?? 0} spelare, tour ${incomingTourId ?? 'saknas'}`);
      } else if (type === 'tour.created') {
        const payload = message as TourCreatedEvent;
        const id = payload.tourId ?? payload.tourId;
        const name = payload.tourName ?? payload.TourName ?? null;
        if (id) {
          setTourId(id);
          if (name) setTourName(name);
          setStatusNote(`Tour skapad: ${name ?? id}`);
        }
      } else if (type === 'player_error') {
        const payload = message as PlayersErrorEvent;
        setError(payload.error ?? payload.Error ?? t('players.serviceError'));
        setStatusNote('Fel från players via websocket.');
      } else if ((message as TourUpdateEvent).tourId ?? (message as TourUpdateEvent).TourId) {
        const payload = message as TourUpdateEvent;
        const incomingTourId = payload.tourId ?? payload.TourId;
        const incomingTourName = payload.tourName ?? payload.TourName ?? null;
        if (incomingTourId) {
          setTourId(incomingTourId);
          if (incomingTourName) setTourName(incomingTourName);
          setError(null);
          setStatusNote(`Tour uppdaterad: ${incomingTourName ?? incomingTourId}`);
        }
      } else if (type === 'round.initiate.ack') {
        const payload = message as {
          roundId?: string;
          tourId?: string;
          requestId?: string;
        };
        const ackRequestId = payload.requestId ?? payload.requestId ?? null;
        if (pendingRoundRequestIdRef.current && ackRequestId && ackRequestId !== pendingRoundRequestIdRef.current) {
            return;
        }
        const ackRoundId = payload.roundId ?? payload.roundId;
        const ackTourId = payload.tourId ?? payload.tourId ?? tourId;
        pendingRoundRequestIdRef.current = null;
        clearRoundAckTimeout();
        setStarting(false);
        if (ackTourId) {
          setTourId(ackTourId);
        }
        if (ackRoundId) {
          const socketClient = getSharedBffWebSocket(apiBaseUrl);
          socketClient.send({ type: 'round.join', roundId: ackRoundId });
          socketClient.send({ type: 'round.requestState', roundId: ackRoundId });
          setCurrentRoundId(ackRoundId);
          setCurrentHole(1);
          const params: string[] = [];
          if (debug) params.push('debug=true');
          if (ackTourId) params.push(`tourId=${ackTourId}`);
          const query = params.length ? `?${params.join('&')}` : '';
          router.push(`/round/${ackRoundId}/hole/1/compact${query}`);
          logToBff('selectGames.round.initiate.ack', { roundId: ackRoundId, tourId: ackTourId, requestId: ackRequestId });
        } else {
          setError('BFF svarade utan roundId.');
          setStatusNote('Kunde inte starta rundan – inget roundId i svaret.');
          logToBff('selectGames.round.initiate.ack.missingRoundId', { tourId: ackTourId, requestId: ackRequestId }, 'warn');
        }
      }
    });
    const unsubscribeErrors = socket.onError((msg) => setError(msg));
    const unsubscribeConnection = socket.onConnectionChange((state) => {
      if (state === 'connected') {
        setError(null);
        if (user?.token) {
          fetchSnapshot();
          fetchGames();
        }
      }
    });

    return () => {
      unsubscribeMessages();
      unsubscribeErrors();
      unsubscribeConnection();
      clearRoundAckTimeout();
    };
  }, [apiBaseUrl, user?.token, router, isNavigationReady, debug, gamesError, logToBff, tourId]);

  useEffect(() => {
    if (!roundIdIsValid || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchRound = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as RoundResponse;
        if (cancelled) return;
        const incomingCourse = data.courseName ?? data.CourseName ?? null;
        const incomingTourId = data.tourId ?? data.TourId ?? null;
        const incomingStatus = data.status ?? data.Status ?? null;
        if (incomingCourse) setCourseName(incomingCourse);
        if (incomingTourId && !tourId) setTourId(incomingTourId);
        if (incomingStatus) setRoundStatus(String(incomingStatus));
        const incomingGameIds = data.gameDefinitionIds ?? data.GameDefinitionIds ?? [];
        if (incomingGameIds.length > 0 && !prefilledGamesRef.current) {
          setSelectedGameIds(new Set(incomingGameIds));
          prefilledGamesRef.current = true;
        }
        const incomingWeights = data.gameWeights ?? data.GameWeights ?? [];
        if (incomingWeights.length > 0 && !prefilledWeightsRef.current) {
          const parsedWeights: Record<string, number> = {};
          incomingWeights.forEach((entry) => {
            if (entry.gameDefinitionId && typeof entry.weightPercent === 'number' && !Number.isNaN(entry.weightPercent)) {
              parsedWeights[entry.gameDefinitionId] = entry.weightPercent;
            }
          });
          if (Object.keys(parsedWeights).length > 0) {
            setWeightsByGameId((prev) => ({ ...parsedWeights, ...prev }));
            prefilledWeightsRef.current = true;
          }
        }
      } catch {
        // Ignore; fallback to GUIDs.
      }
    };
    fetchRound();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, tourId, user?.token]);

  useEffect(() => {
    if (!roundIdIsValid || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchRoundPlayers = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          if (!cancelled) setRoundPlayerCount(null);
          return;
        }
        const payload = await response.json();
        const count = Array.isArray(payload)
          ? payload.length
          : Array.isArray((payload as { players?: unknown[] }).players)
            ? ((payload as { players?: unknown[] }).players?.length ?? 0)
            : 0;
        if (!cancelled) {
          setRoundPlayerCount(count > 0 ? count : null);
        }
      } catch {
        if (!cancelled) setRoundPlayerCount(null);
      }
    };
    fetchRoundPlayers();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, user?.token]);

  useEffect(() => {
    if (!tourId || !user?.token || !isNavigationReady) return;
    let cancelled = false;
    const fetchTour = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as TourDetailsResponse;
        if (cancelled) return;
        const incomingTourName = data.tourName ?? data.TourName ?? data.name ?? data.Name ?? null;
        if (incomingTourName) setTourName(incomingTourName);
        const incomingTournamentGameIds = Array.from(
          new Set(
            [
              ...(data.gameDefinitionIds ?? []),
              ...(data.GameDefinitionIds ?? []),
            ].filter(Boolean)
          )
        );
        setTournamentGameIds(incomingTournamentGameIds);
        if (incomingTournamentGameIds.length > 0) {
          setSelectedGameIds((prev) => {
            const next = new Set(prev);
            incomingTournamentGameIds.forEach((gameId) => next.add(gameId));
            return next;
          });
        }
      } catch {
        // Ignore; fallback to GUIDs.
      }
    };
    fetchTour();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, tourId, user?.token]);

  useEffect(() => {
    if (tournamentGameIds.length === 0) return;
    setSelectedGameIds((prev) => ensureTournamentGamesSelected(prev, tournamentGameIds));
  }, [tournamentGameIds]);

  useEffect(() => {
    if (!user?.token || recommendationPlayerCount <= 0 || gameDefs.length === 0) {
      setRecommendedPriorityByGameId({});
      return;
    }

    let cancelled = false;
    const fetchRecommendations = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-recommendations?playerCount=${recommendationPlayerCount}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) {
          logToBff('selectGames.recommendations.load.failed', { playerCount: recommendationPlayerCount, status: response.status }, 'warn');
          if (!cancelled) {
            setRecommendedPriorityByGameId({});
          }
          return;
        }

        const payload = (await response.json()) as GameRecommendation[];
        if (cancelled) return;
        const next: Record<string, number> = {};
        (payload ?? []).forEach((entry) => {
          const gameDefinitionId = entry.gameDefinitionId ?? entry.GameDefinitionId;
          const priority = entry.priority ?? entry.Priority;
          if (!gameDefinitionId || typeof priority !== 'number' || Number.isNaN(priority)) {
            return;
          }
          next[gameDefinitionId] = priority;
        });
        setRecommendedPriorityByGameId(next);
      } catch {
        if (!cancelled) {
          setRecommendedPriorityByGameId({});
        }
      }
    };

    fetchRecommendations();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, gameDefs.length, logToBff, recommendationPlayerCount, user?.token]);

  const toggleGame = (id: string) => {
    setSelectedGameIds((prev) => toggleSelectableGame(prev, id, tournamentGameIdSet));
  };

  const scoringPointsByGameId = useGameScoringRules(apiBaseUrl, user?.token, effectivePlayerCount);

  const getGameWeightPercent = (gameId: string) =>
    weightsByGameId[gameId] ?? resolveDefaultGameWeightPercent(gameDefs, gameId);

  const cycleGameWeight = (gameId: string) => {
    if (tournamentGameIdSet.has(gameId)) return;
    setWeightsByGameId((prev) => {
      const current = prev[gameId] ?? resolveDefaultGameWeightPercent(gameDefs, gameId);
      const normalized = (WEIGHT_STEPS as readonly number[]).includes(current) ? current : 100;
      const nextIndex = ((WEIGHT_STEPS as readonly number[]).indexOf(normalized) + 1) % WEIGHT_STEPS.length;
      return { ...prev, [gameId]: WEIGHT_STEPS[nextIndex] };
    });
  };

  const meritDistributionEntries = useMemo(
    () =>
      sortedGameDefs
        .filter((game) => selectedGameIds.has(game.id))
        .map((game) => ({
          gameId: game.id,
          name: getGameDisplayName(game, t('selectGames.gameNameFallback')),
          ladder: getMeritLadder(
            scoringPointsByGameId,
            game.id,
            weightsByGameId[game.id] ?? resolveDefaultGameWeightPercent(gameDefs, game.id),
          ),
        })),
    [gameDefs, scoringPointsByGameId, selectedGameIds, sortedGameDefs, weightsByGameId],
  );

  const startGame = () => {
    if (selectedGameIds.size === 0 || !tourId || !roundIdIsValid) {
      if (!roundId) {
        setError('Ingen runda att fortsätta. Gå tillbaka och starta om.');
      } else {
        setError(null);
      }
      return;
    }
    setStarting(true);
    setError(null);
    setStatusNote('Uppdaterar rundan via websocket...');
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    logToBff('selectGames.update.clicked', { tourId, gameCount: selectedGameIds.size, playerCount: effectivePlayerCount });
    const payload = {
      type: 'round.update',
      roundId,
      tourId,
      gameDefinitionIds: Array.from(selectedGameIds),
      gameWeights: Array.from(selectedGameIds).map((id) => ({
        gameDefinitionId: id,
        weightPercent: getGameWeightPercent(id),
      })),
    };
    const sent = socket.send(payload);
    if (!sent) {
      setStatusNote('Väntar på websocket-anslutning...');
      logToBff('selectGames.round.update.queued', { tourId, gameCount: selectedGameIds.size });
    } else {
      logToBff('selectGames.round.update.sent', { tourId, gameCount: selectedGameIds.size });
    }
    const selectedIds = Array.from(selectedGameIds);
    const gameDefinitionTypes: Record<string, string> = {};
    gameDefs.forEach((game) => {
      gameDefinitionTypes[game.id] = (game.competitionType ?? '').toLowerCase();
    });
    const teamGamesSelected = selectedIds.some((id) => gameDefinitionTypes[id] === 'team');
    const mysterySelected = Array.from(selectedGameIds).some((id) =>
      gameDefs.some((g) => g.id === id && (g.name ?? '').toLowerCase() === 'mystery')
    );
    const params: string[] = [];
    if (debug) params.push('debug=true');
    if (tourId) params.push(`tourId=${tourId}`);
    const selectedIdsParam = selectedIds.join(',');
    if (selectedIdsParam) params.push(`gameDefinitionIds=${encodeURIComponent(selectedIdsParam)}`);
    if (mysterySelected && teamGamesSelected) params.push('teamGame=1');
    const query = params.length ? `?${params.join('&')}` : '';
    if (openedFromRoundStart) {
      router.push(`/round/${roundId}/roundstart${query}`);
      setStarting(false);
      return;
    }
    if (mysterySelected) {
      router.push(`/round/${roundId}/mystery-prize${query}`);
    } else {
      const route = resolvePostGameSelectionRoute({
        roundId,
        debug,
        tourId,
        selectedGameDefinitionIds: selectedIds,
        gameDefinitionTypes,
        hasMysteryGame: mysterySelected,
        source: sourceFromParams || 'select-games',
      });
      if (route.path.includes('/hole/1/compact')) {
        setCurrentHole(1);
      }
      router.push(buildRoundRouteHref(route));
    }
    setStarting(false);
  };

  const renderGameTile = (game: GameDefinition) => {
    const isSelected = selectedGameIds.has(game.id);
    const isLocked = tournamentGameIdSet.has(game.id);
    const isRecommended = Number.isFinite(recommendedPriorityByGameId[game.id]);
    const recommendationRank = getRecommendationRank(game.id);
    const imageSource = resolveGameImageSource(game.image, game.name);
    const hasImage = !!imageSource;
    const gameWeightPercent = getGameWeightPercent(game.id);
    const meritLadderLabel = isSelected
      ? formatMeritLadder(getMeritLadder(scoringPointsByGameId, game.id, gameWeightPercent))
      : '';
    const isTeamGame = (game.competitionType ?? '').toLowerCase() === 'team';
    return (
      <Pressable
        key={game.id}
        onPress={() => toggleGame(game.id)}
        style={[
          styles.gameTile,
          gameTileStyle,
          isSelected && styles.gameTileSelected,
          !isRecommended && styles.gameTileNotRecommended,
          isLocked && styles.gameTileLocked,
        ]}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isSelected, disabled: isLocked }}
      >
        <Text style={styles.gameTileName}>{getGameDisplayName(game, t('selectGames.gameNameFallback'))}</Text>
        {isRecommended ? (
          <View style={styles.recommendedBadgeRow}>
            <Text
              style={styles.recommendedRibbon}
              accessibilityLabel={`Recommended for ${recommendationPlayerCountLabel} players`}
            >
              Recommended for {recommendationPlayerCountLabel} players
            </Text>
            {recommendationRank ? (
              <Text style={styles.recommendedRankChip} accessibilityLabel={`Recommendation rank ${recommendationRank}`}>
                #{recommendationRank}
              </Text>
            ) : null}
          </View>
        ) : null}
        {hasImage ? (
          <View style={styles.gameImageWrap}>
            <Image source={imageSource} style={[styles.gameImage, gameImageStyle, isSelected && styles.gameImageSelected]} resizeMode="cover" />
            {isSelected ? (
              <View style={styles.selectedCheckBadge}>
                <Text style={styles.selectedCheckText}>✓</Text>
              </View>
            ) : null}
            <Pressable
              style={styles.infoButton}
              onPress={() => {
                setInfoGame(game);
                setInfoOpen(true);
              }}
              accessibilityRole="button"
              accessibilityLabel={t('selectGames.gameInfo')}
            >
              <Text style={styles.infoButtonText}>{t('selectGames.infoIcon')}</Text>
            </Pressable>
          </View>
        ) : (
          <View style={[styles.gameImageEmpty, gameImageStyle]} />
        )}
        {isSelected && meritLadderLabel ? (
          <View style={styles.meritRow}>
            <GameWeightControl
              weightPercent={gameWeightPercent}
              onPress={() => cycleGameWeight(game.id)}
              selected
              showLabel={false}
              size="compact"
              disabled={isLocked}
              accessibilityLabel={t('selectGames.meritLadderA11y', {
                name: getGameDisplayName(game, t('selectGames.gameNameFallback')),
                ladder: meritLadderLabel,
                weight: gameWeightPercent,
              })}
            />
            <View style={styles.meritTextWrap}>
              <Text style={styles.meritLadderText} numberOfLines={1}>
                {meritLadderLabel}
              </Text>
              <Text style={[styles.meritCaption, isLocked && styles.meritCaptionLocked]} numberOfLines={1}>
                {isLocked
                  ? t('selectGames.meritWeightSetByTour')
                  : isTeamGame
                    ? t('selectGames.meritsPerTeamLabel')
                    : t('selectGames.meritsLabel')}
              </Text>
            </View>
          </View>
        ) : null}
        {isLocked ? (
          <Text style={styles.gameTileLockedLabel}>{t('selectGames.tournamentManagedLocked')}</Text>
        ) : null}
      </Pressable>
    );
  };

  if (!isNavigationReady) {
    return null;
  }

  if (!user) {
    return <Redirect href="/" />;
  }

  if (!navState?.key) {
    return null;
  }

  if (!roundId) {
    const debugParam = debug ? '?debug=true' : '';
    const backPath = tourId ? `/tour/${tourId}/round-init${debugParam}` : '/my-tours';
    return (
      <View style={styles.errorPage}>
        <Text style={styles.title}>Ingen runda hittades</Text>
        <Text style={styles.errorMeta}>Gå tillbaka och starta rundan igen.</Text>
        <Text style={styles.errorSupport}>Om felet kvarstår, kontakta support.</Text>
        <Pressable style={styles.errorButton} onPress={() => router.replace(backPath)}>
          <Text style={styles.errorButtonText}>Tillbaka</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <>
      <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <View style={styles.heroImageWrap}>
        <Image source={chooseGameImage} style={styles.heroImage} resizeMode="cover" />
        <Text style={styles.title}>{t('selectGames.title')}</Text>
      </View>
      <UserBadge debug={debug} />
      <View style={styles.statusCard}>
        {debug ? <Text style={styles.statusText}>{statusNote}</Text> : null}
        <Text style={styles.statusText}>
          {t('selectGames.tourLabel')}: {stripGuidSuffix(tourName) ?? tourId ?? t('selectGames.missing')}
        </Text>
        <Text style={styles.statusText}>
          {t('selectGames.roundLabel')}: {stripGuidSuffix(courseName) ?? roundId ?? t('selectGames.missing')}
        </Text>
        <View style={styles.playersPill} accessibilityLabel={`Players: ${effectivePlayerCount}`}>
          <Text style={styles.playersPillText}>Players: {effectivePlayerCount}</Text>
        </View>
      </View>

      <View style={styles.card}>
        <Text style={styles.sectionTitle}>{t('selectGames.gameListTitle')}</Text>
        {meritDistributionEntries.length > 0 ? (
          <View style={styles.meritDistributionWrap}>
            <MeritDistributionBar entries={meritDistributionEntries} />
          </View>
        ) : null}
        {groupedGameDefs.map((group) => (
          <View key={group.groupId} style={styles.gameGroup}>
            <Text style={styles.groupHeader}>{group.title}</Text>
            <View style={styles.gameGrid}>
              {group.items.map(renderGameTile)}
            </View>
          </View>
        ))}
        {gamesError ? <Text style={styles.error}>{gamesError}</Text> : null}
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}
      <GoffyButton
        label={
          starting
            ? 'Startar...'
            : openedFromRoundStart
              ? t('roundStartSetup.openRoundCta')
              : openedFromHole
                ? t('selectGames.updateCta')
              : isRoundAlreadyActive
                ? t('selectGames.updateCta')
                : t('selectGames.startRound')
        }
        onPress={startGame}
        disabled={selectedGameIds.size === 0 || !tourId || starting}
        tone="accent"
        size="compact"
        style={styles.selectGamesButton}
      />
      </ScrollView>
      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={closeInfo}
      />
    </>
  );
}

function isGuid(value: string | null | undefined) {
  if (!value) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);
}

function resolveDefaultGameWeightPercent(gameDefs: GameDefinition[], gameId: string): number {
  const game = gameDefs.find((candidate) => candidate.id === gameId);
  const weight = game?.defaultWeightPercent;
  if (typeof weight !== 'number' || Number.isNaN(weight)) {
    return DEFAULT_GAME_WEIGHT_PERCENT;
  }

  return Math.max(0, Math.min(100, Math.round(weight)));
}

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function stripGuidSuffix(value?: string | null) {
  if (!value) return value;
  return value.replace(/\s*[0-9a-fA-F]{32}$/u, '').replace(/\s*[0-9a-fA-F-]{36}$/u, '').trim();
}

function getGameImageSource(name?: string | null): ImageSourcePropType {
  const slug = getGameSlug(name);
  if (!slug) return getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH);
  return GAME_IMAGE_MAP[slug] ?? getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH);
}

function resolveGameImageSource(imagePath: string | undefined, name?: string | null): ImageSourcePropType {
  const uri = resolveGameImageUri(imagePath);
  if (uri) {
    return { uri };
  }
  return getGameImageSource(name);
}

function resolveGameImageUri(imagePath: string | undefined): string | null {
  const raw = (imagePath ?? '').trim();
  if (!raw) return null;

  const fromUnstableAssetPath = resolveImageUriFromUnstableAssetPath(raw);
  if (fromUnstableAssetPath) {
    return fromUnstableAssetPath;
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('/assets/')) {
    // Static Expo asset URLs should not be used for runtime game media.
    // Fall back to canonical image mapping when no stable media key can be derived.
    return null;
  }

  const normalized = raw.replace(/^\/+/, '');

  if (normalized.startsWith('assets-generated/images/') || normalized.startsWith('images/')) {
    return buildMediaUrl('images', normalized, 'game-select', { pagePath: MEDIA_PAGE_PATH });
  }

  if (raw.startsWith('s3://')) {
    const withoutScheme = raw.slice('s3://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0 && slashIndex + 1 < withoutScheme.length) {
      const key = withoutScheme.slice(slashIndex + 1).replace(/^\/+/, '');
      if (key.startsWith('assets-generated/images/') || key.startsWith('images/')) {
        return buildMediaUrl('images', key, 'game-select', { pagePath: MEDIA_PAGE_PATH });
      }
    }
  }

  return null;
}

function resolveImageUriFromUnstableAssetPath(raw: string): string | null {
  try {
    const parsed = new URL(raw, 'http://localhost');
    if (!parsed.pathname.startsWith('/assets/')) {
      return null;
    }

    const unstablePath = parsed.searchParams.get('unstable_path');
    if (!unstablePath) {
      return null;
    }

    let normalized = decodeURIComponent(unstablePath).trim().replace(/\\/g, '/');
    normalized = normalized.replace(/^[./]+/, '');

    if (normalized.startsWith('assets-generated/images/') || normalized.startsWith('images/')) {
      return buildMediaUrl('images', normalized, 'game-select', { pagePath: MEDIA_PAGE_PATH });
    }
  } catch {
    return null;
  }

  return null;
}

function getGameSlug(name?: string | null) {
  const fromName = (name ?? '').trim();
  if (!fromName) {
    return '';
  }
  return fromName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 3px 9px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
  },
});

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: appColors.background.app,
  },
  heroImage: {
    width: '100%',
    height: 140,
    borderRadius: 18,
  },
  heroImageWrap: {
    position: 'relative',
    width: '100%',
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 12,
    justifyContent: 'center',
    backgroundColor: appColors.background.app,
  },
  hero: {
    fontSize: 52,
    textAlign: 'center',
    marginBottom: 4,
  },
  title: {
    position: 'absolute',
    top: 2,
    left: 16,
    right: 16,
    zIndex: 2,
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    color: appColors.text.heading,
    textShadow: 'none',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 12,
    alignSelf: 'center',
  },
  statusCard: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 4,
  },
  playersPill: {
    marginTop: 8,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: '#0f3f24',
    borderWidth: 1,
    borderColor: '#2f855a',
  },
  playersPillText: {
    color: '#d9f99d',
    fontSize: 12,
    fontWeight: '700',
  },
  statusText: {
    fontSize: 13,
    color: appColors.text.secondary,
  },
  errorPage: {
    flex: 1,
    backgroundColor: appColors.background.app,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 12,
  },
  errorMeta: {
    color: '#fca5a5',
    textAlign: 'center',
  },
  errorSupport: {
    color: appColors.accent.primary,
    textAlign: 'center',
  },
  errorButton: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
    backgroundColor: appColors.background.interactive,
  },
  errorButtonText: {
    color: appColors.text.inverse,
    fontWeight: '700',
  },
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    gap: 12,
    ...cardShadow,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  gameGroup: {
    width: '100%',
    gap: 10,
  },
  groupHeader: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  gameGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 12,
  },
  gameTile: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 8,
    minHeight: 88,
    ...cardShadow,
  },
  gameTileSelected: {
    borderColor: appColors.border.accent,
  },
  gameTileNotRecommended: {
    opacity: 0.84,
    borderColor: appColors.border.default,
  },
  gameTileLocked: {
    borderColor: '#f59e0b',
    backgroundColor: '#251603',
  },
  gameImage: {
    width: '100%',
    height: 140,
    borderRadius: 10,
  },
  gameImageWrap: {
    position: 'relative',
  },
  gameImageEmpty: {
    width: '100%',
    height: 140,
    borderRadius: 10,
    backgroundColor: appColors.background.panelAlt,
  },
  infoButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#F2FAF1',
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  selectedCheckBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 6,
    backgroundColor: '#16a34a',
    opacity: 1,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  },
  selectedCheckText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 14,
  },
  infoButtonText: {
    color: '#0B2E1A',
    fontSize: 15,
    fontWeight: '800',
  },
  gameImageSelected: {
    opacity: 0.6,
  },
  gameTileName: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.primary,
    textAlign: 'center',
    marginBottom: 6,
  },
  recommendedBadgeRow: {
    marginBottom: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 6,
  },
  recommendedRibbon: {
    flexShrink: 1,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    backgroundColor: '#facc15',
    color: '#1f2937',
    fontSize: 10,
    fontWeight: '800',
    overflow: 'hidden',
  },
  recommendedRankChip: {
    minWidth: 30,
    paddingHorizontal: 8,
    paddingVertical: 3,
    textAlign: 'center',
    borderRadius: 10,
    backgroundColor: '#334155',
    color: '#e2e8f0',
    fontSize: 10,
    fontWeight: '800',
  },
  gameTileLockedLabel: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: '700',
    color: '#fcd34d',
    textAlign: 'center',
  },
  meritDistributionWrap: {
    marginBottom: 12,
  },
  meritRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  meritTextWrap: {
    flexShrink: 1,
  },
  meritLadderText: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meritCaption: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  meritCaptionLocked: {
    color: '#fcd34d',
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    color: appColors.text.primary,
  },
  tourId: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.accent.primary,
    textAlign: 'center',
  },
  spinner: {
    marginTop: 4,
  },
  error: {
    color: '#ff7b7b',
  },
  selectGamesButton: {
    marginTop: 8,
    alignSelf: 'center',
  },
});
